/**
 * COMP-53: Match Result Entry Modal
 * Modal for entering match results with score validation
 */

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  XMarkIcon,
  TrophyIcon,
  ClockIcon,
  UserIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { Match, Participant } from '../../types/tournament';

const matchResultSchema = z.object({
  score1: z.number().min(0, 'Score must be non-negative').optional(),
  score2: z.number().min(0, 'Score must be non-negative').optional(),
  winnerId: z.number().optional(),
  status: z.enum(['pending', 'in_progress', 'completed', 'cancelled']),
  notes: z.string().optional(),
  scheduledAt: z.string().optional(),
  location: z.string().optional()
});

type MatchResultForm = z.infer<typeof matchResultSchema>;

interface MatchResultModalProps {
  match: Partial<Match> & { id: string };
  participants: Array<Partial<Participant> & { id: number; name: string }>;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (matchId: string, updates: Partial<Match>) => void;
  isLoading?: boolean;
}

export const MatchResultModal: React.FC<MatchResultModalProps> = ({
  match,
  participants,
  isOpen,
  onClose,
  onSubmit,
  isLoading = false
}) => {
  const [selectedWinner, setSelectedWinner] = useState<number | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isValid }
  } = useForm<MatchResultForm>({
    resolver: zodResolver(matchResultSchema),
    defaultValues: {
      score1: match.score1 || undefined,
      score2: match.score2 || undefined,
      winnerId: match.winnerId || undefined,
      status: match.status,
      notes: match.notes || '',
      scheduledAt: match.scheduledAt ? new Date(match.scheduledAt).toISOString().slice(0, 16) : '',
      location: match.notes || ''
    }
  });

  const score1 = watch('score1');
  const score2 = watch('score2');
  const status = watch('status');

  // Get participant details
  const participant1 = participants.find(p => p.id === match.participant1Id);
  const participant2 = participants.find(p => p.id === match.participant2Id);

  // Auto-determine winner based on scores
  useEffect(() => {
    if (score1 !== undefined && score2 !== undefined && score1 !== score2) {
      const winnerId = score1 > score2 ? match.participant1Id : match.participant2Id;
      if (winnerId) {
        setSelectedWinner(winnerId);
        setValue('winnerId', winnerId);
      }
    }
  }, [score1, score2, match.participant1Id, match.participant2Id, setValue]);

  // Reset form when match changes
  useEffect(() => {
    if (match) {
      reset({
        score1: match.score1 || undefined,
        score2: match.score2 || undefined,
        winnerId: match.winnerId || undefined,
        status: match.status,
        notes: match.notes || '',
        scheduledAt: match.scheduledAt ? new Date(match.scheduledAt).toISOString().slice(0, 16) : '',
        location: match.notes || ''
      });
      setSelectedWinner(match.winnerId || null);
    }
  }, [match, reset]);

  const onFormSubmit = (data: MatchResultForm) => {
    const updates: Partial<Match> = {
      ...data,
      scheduledAt: data.scheduledAt ? new Date(data.scheduledAt) : undefined
    };

    // If scores are provided and status is pending, auto-set to completed
    if (data.score1 !== undefined && data.score2 !== undefined && status === 'pending') {
      updates.status = 'completed';
    }

    onSubmit(match.id, updates);
  };

  const handleWinnerSelect = (participantId: number) => {
    setSelectedWinner(participantId);
    setValue('winnerId', participantId);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'in_progress': return 'text-yellow-600 bg-yellow-100';
      case 'cancelled': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto border border-gray-200">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <TrophyIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Match Result Entry</h2>
              <p className="text-sm text-gray-600">Round {match.round} - Position {match.position}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-white/50 transition-all duration-200"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onFormSubmit)} className="p-6 space-y-6">
          {/* Match Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Match Status
            </label>
            <select
              {...register('status')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Participants and Scores */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Participant 1 */}
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <UserIcon className="h-5 w-5 text-gray-400" />
                <span className="font-medium text-gray-900">
                  {participant1?.name || 'TBD'}
                </span>
                {selectedWinner === match.participant1Id && (
                  <CheckCircleIcon className="h-5 w-5 text-green-500" />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Score
                </label>
                <input
                  type="number"
                  min="0"
                  {...register('score1', { valueAsNumber: true })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0"
                />
                {errors.score1 && (
                  <p className="text-sm text-red-600 mt-1">{errors.score1.message}</p>
                )}
              </div>
              {participant1 && (
                <button
                  type="button"
                  onClick={() => handleWinnerSelect(participant1.id)}
                  className={`w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedWinner === participant1.id
                      ? 'bg-green-100 text-green-700 border-2 border-green-300'
                      : 'bg-gray-100 text-gray-700 border-2 border-gray-300 hover:bg-gray-200'
                  }`}
                >
                  {selectedWinner === participant1.id ? 'Winner' : 'Select Winner'}
                </button>
              )}
            </div>

            {/* Participant 2 */}
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <UserIcon className="h-5 w-5 text-gray-400" />
                <span className="font-medium text-gray-900">
                  {participant2?.name || 'TBD'}
                </span>
                {selectedWinner === match.participant2Id && (
                  <CheckCircleIcon className="h-5 w-5 text-green-500" />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Score
                </label>
                <input
                  type="number"
                  min="0"
                  {...register('score2', { valueAsNumber: true })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0"
                />
                {errors.score2 && (
                  <p className="text-sm text-red-600 mt-1">{errors.score2.message}</p>
                )}
              </div>
              {participant2 && (
                <button
                  type="button"
                  onClick={() => handleWinnerSelect(participant2.id)}
                  className={`w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedWinner === participant2.id
                      ? 'bg-green-100 text-green-700 border-2 border-green-300'
                      : 'bg-gray-100 text-gray-700 border-2 border-gray-300 hover:bg-gray-200'
                  }`}
                >
                  {selectedWinner === participant2.id ? 'Winner' : 'Select Winner'}
                </button>
              )}
            </div>
          </div>

          {/* Schedule and Location */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <ClockIcon className="h-4 w-4 inline mr-1" />
                Scheduled Time
              </label>
              <input
                type="datetime-local"
                {...register('scheduledAt')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location
              </label>
              <input
                type="text"
                {...register('location')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Court 1, Gym A, etc."
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              {...register('notes')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Additional notes about the match..."
            />
          </div>

          {/* Current Status Display */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Current Status:</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
                {status.replace('_', ' ').toUpperCase()}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isValid || isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {isLoading ? 'Saving...' : 'Save Result'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MatchResultModal;
